/** Open-Meteo WMO weather code → 한글 요약 */
const WEATHER_KO: Record<number, string> = {
  0: "맑음",
  1: "대체로 맑음",
  2: "부분 구름",
  3: "흐림",
  45: "안개",
  48: "착빙 안개",
  51: "이슬비",
  53: "이슬비",
  55: "강한 이슬비",
  61: "비",
  63: "비",
  65: "폭우",
  71: "눈",
  73: "눈",
  75: "폭설",
  80: "소나기",
  81: "소나기",
  82: "강한 소나기",
  95: "뇌우",
  96: "뇌우·우박",
  99: "뇌우·우박",
};

export interface WeatherCityDef {
  id: string;
  name: string;
  country: string;
  lat: number;
  lon: number;
}

export interface CityWeather {
  id: string;
  name: string;
  country: string;
  temperatureC: number;
  weatherCode: number;
  summary: string;
}

export interface MultiCityWeatherSnapshot {
  cities: CityWeather[];
  updatedAt: string;
}

/** 선택 가능한 도시 카탈로그 */
export const WEATHER_CITY_CATALOG: WeatherCityDef[] = [
  { id: "seoul", name: "서울", country: "KR", lat: 37.5665, lon: 126.978 },
  { id: "busan", name: "부산", country: "KR", lat: 35.1796, lon: 129.0756 },
  {
    id: "shenzhen",
    name: "심천",
    country: "CN",
    lat: 22.5431,
    lon: 114.0579,
  },
  { id: "shanghai", name: "상하이", country: "CN", lat: 31.2304, lon: 121.4737 },
  { id: "tokyo", name: "도쿄", country: "JP", lat: 35.6762, lon: 139.6503 },
  { id: "osaka", name: "오사카", country: "JP", lat: 34.6937, lon: 135.5023 },
  { id: "singapore", name: "싱가포르", country: "SG", lat: 1.3521, lon: 103.8198 },
  { id: "hanoi", name: "하노이", country: "VN", lat: 21.0278, lon: 105.8342 },
  { id: "newyork", name: "뉴욕", country: "US", lat: 40.7128, lon: -74.006 },
  { id: "frankfurt", name: "프랑크푸르트", country: "DE", lat: 50.1109, lon: 8.6821 },
];

/** 기본 선택: 서울 · 심천 · 도쿄 */
export const DEFAULT_WEATHER_CITY_IDS = ["seoul", "shenzhen", "tokyo"] as const;

export const WEATHER_CITY_STORAGE_KEY = "mes_login_weather_cities";
export const MAX_WEATHER_CITIES = 4;

export function getDefaultWeatherCities(): WeatherCityDef[] {
  return DEFAULT_WEATHER_CITY_IDS.map(
    (id) => WEATHER_CITY_CATALOG.find((c) => c.id === id)!
  ).filter(Boolean);
}

export function loadSelectedWeatherCities(): WeatherCityDef[] {
  try {
    const raw = localStorage.getItem(WEATHER_CITY_STORAGE_KEY);
    if (!raw) return getDefaultWeatherCities();
    const ids = JSON.parse(raw) as string[];
    if (!Array.isArray(ids) || ids.length === 0) {
      return getDefaultWeatherCities();
    }
    const cities = ids
      .map((id) => WEATHER_CITY_CATALOG.find((c) => c.id === id))
      .filter((c): c is WeatherCityDef => Boolean(c))
      .slice(0, MAX_WEATHER_CITIES);
    return cities.length > 0 ? cities : getDefaultWeatherCities();
  } catch {
    return getDefaultWeatherCities();
  }
}

export function saveSelectedWeatherCityIds(ids: string[]): void {
  localStorage.setItem(
    WEATHER_CITY_STORAGE_KEY,
    JSON.stringify(ids.slice(0, MAX_WEATHER_CITIES))
  );
}

interface OpenMeteoCurrent {
  temperature_2m?: number;
  weather_code?: number;
  time?: string;
}

interface OpenMeteoResponse {
  current?: OpenMeteoCurrent;
}

/** 여러 도시 날씨 일괄 조회 (Open-Meteo, API 키 불필요) */
export async function fetchMultiCityWeather(
  cities: WeatherCityDef[]
): Promise<MultiCityWeatherSnapshot> {
  if (cities.length === 0) {
    return { cities: [], updatedAt: new Date().toISOString() };
  }

  const lat = cities.map((c) => c.lat).join(",");
  const lon = cities.map((c) => c.lon).join(",");
  const url =
    `https://api.open-meteo.com/v1/forecast` +
    `?latitude=${lat}&longitude=${lon}` +
    `&current=temperature_2m,weather_code` +
    `&timezone=auto`;

  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`WEATHER_HTTP_${res.status}`);
  }

  const data = await res.json();
  const list: OpenMeteoResponse[] = Array.isArray(data) ? data : [data];

  const result: CityWeather[] = cities.map((city, i) => {
    const entry = list[i] ?? list[0];
    const cur = entry?.current;
    const code = cur?.weather_code ?? 0;
    return {
      id: city.id,
      name: city.name,
      country: city.country,
      temperatureC: cur?.temperature_2m ?? 0,
      weatherCode: code,
      summary: WEATHER_KO[code] ?? "—",
    };
  });

  return {
    cities: result,
    updatedAt: list[0]?.current?.time ?? new Date().toISOString(),
  };
}
